/**
 * CalendarReconcileButton — admin-only Bookings-tab action that
 * recovers confirmed bookings whose Google Calendar event silently
 * dropped (rate limit, 403, network flake, etc.).
 *
 * Two-tap flow, mirrors the "Backfill historical margins" button in
 * JobProfitability.jsx:
 *   1) Tap "Recover missing calendar events" → GET dry-run, dialog
 *      opens with a preview of every affected booking (name / date /
 *      time / current sync status).
 *   2) Tap "Confirm & recover" inside the dialog → POST, dialog swaps
 *      to a result summary (synced / still_failing / per-booking
 *      errors).
 *
 * A dry-run of 0 misses shows a friendly "All bookings are synced"
 * state — no confirm button, so staff don't accidentally kick off a
 * no-op mutation.
 *
 * Backend endpoints (both admin-gated):
 *   GET  /api/bookings/admin/gcal-reconcile  (dry-run report)
 *   POST /api/bookings/admin/gcal-reconcile  (idempotent recovery)
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarCheck2, CheckCircle2, AlertTriangle } from "lucide-react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CalendarReconcileButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // `plan` is the dry-run response { future_count, past_count, future: [], past: [], ... }
  // `result` is the POST response { attempted, synced, still_failing, included_past, errors }
  // Only one is shown at a time — plan first, then result once confirmed.
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  // Opt-in to also recover past bookings — off by default. Recovering
  // a past booking creates a Calendar event for an appointment that
  // already happened, which is usually noise, so we make the admin
  // explicitly tick this box.
  const [includePast, setIncludePast] = useState(false);

  // Step 1 — hard-coded GET so a single tap can never mutate data.
  const openPreview = async () => {
    setLoading(true);
    setPlan(null);
    setResult(null);
    setIncludePast(false);
    setOpen(true);
    try {
      const r = await axios.get(`${API}/bookings/admin/gcal-reconcile`);
      setPlan(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Couldn't check calendar sync: ${detail}`);
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — actual recovery. Backend is idempotent, so an accidental
  // double-tap is safe; the button also guards via `loading`.
  const confirmRecover = async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/bookings/admin/gcal-reconcile`,
        null,
        { params: { include_past: includePast } },
      );
      setResult(r.data);
      const s = r.data?.synced ?? 0;
      const f = r.data?.still_failing ?? 0;
      if (f === 0) {
        toast.success(
          `Recovered ${s} calendar event${s === 1 ? "" : "s"}. All bookings are synced.`,
        );
      } else {
        toast.warning(
          `Recovered ${s}, ${f} still failing — see details.`,
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Recovery failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (loading) return; // don't allow closing mid-mutation
    setOpen(false);
    setPlan(null);
    setResult(null);
  };

  const futureCount = plan?.future_count ?? 0;
  const pastCount = plan?.past_count ?? 0;
  const future = Array.isArray(plan?.future) ? plan.future : [];
  const past = Array.isArray(plan?.past) ? plan.past : [];
  const targetCount = futureCount + (includePast ? pastCount : 0);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={openPreview}
        disabled={loading}
        className="border-slate-700 text-slate-300 hover:text-white"
        data-testid="gcal-reconcile-open-btn"
      >
        <CalendarCheck2 className="w-4 h-4 mr-2" />
        Recover missing calendar events
      </Button>

      <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
        <DialogContent
          className="bg-slate-900 border-slate-700 text-white max-w-2xl"
          data-testid="gcal-reconcile-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">
              Recover missing calendar events
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Finds confirmed bookings that never landed on the Google Calendar
              (usually a silent rate-limit or transient API error) and re-syncs
              them. Safe to re-run — the backend is idempotent.
            </DialogDescription>
          </DialogHeader>

          {/* Loading — first render before either plan or result exists */}
          {loading && !plan && !result && (
            <div
              className="flex items-center justify-center py-10 text-slate-400"
              data-testid="gcal-reconcile-loading"
            >
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Checking calendar sync status…
            </div>
          )}

          {/* Result view — takes over once POST completes */}
          {result && (
            <div className="space-y-3" data-testid="gcal-reconcile-result">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-rajdhani font-semibold">
                  Recovered {result.synced ?? 0} of {result.attempted ?? 0} bookings
                </span>
              </div>
              {(result.still_failing ?? 0) > 0 && (
                <div className="rounded-md bg-red-500/10 border border-red-500/40 p-3">
                  <div className="flex items-center gap-2 text-red-300 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-semibold">
                      {result.still_failing} still failing
                    </span>
                  </div>
                  <ul
                    className="text-xs text-red-200 space-y-1 max-h-40 overflow-y-auto"
                    data-testid="gcal-reconcile-errors-list"
                  >
                    {(result.errors || []).map((e) => (
                      <li key={e.booking_id}>
                        <span className="font-mono text-red-300">
                          {e.booking_id.slice(0, 16)}…
                        </span>
                        {" — "}
                        {e.customer_name || "(unknown customer)"}
                        {e.error ? ` · ${e.error}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-red-200/70 mt-2">
                    Grep <span className="font-mono">gcal_error</span> in the
                    backend log for the exact HTTP status per booking.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Preview view — shown after GET completes, before POST */}
          {!result && plan && (
            <div className="space-y-3" data-testid="gcal-reconcile-plan">
              {!plan.gcal_configured && (
                <div
                  className="rounded-md bg-amber-500/10 border border-amber-500/40 p-3 text-amber-200 text-sm"
                  data-testid="gcal-reconcile-not-configured"
                >
                  Google Calendar isn&apos;t configured on this environment. The
                  reconcile endpoint will reject a Confirm tap.
                </div>
              )}

              {futureCount === 0 && pastCount === 0 ? (
                <div
                  className="flex flex-col items-center justify-center py-8 text-center"
                  data-testid="gcal-reconcile-empty"
                >
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-2" />
                  <p className="font-rajdhani font-semibold text-white text-lg">
                    All bookings are synced
                  </p>
                  <p className="text-slate-400 text-sm">
                    No confirmed bookings are missing from the calendar.
                  </p>
                </div>
              ) : (
                <>
                  {/* Future bucket — the default-actionable set */}
                  <div className="rounded-md border border-slate-800 overflow-hidden">
                    <div className="bg-slate-800/60 px-3 py-2 text-xs text-slate-300 flex items-center justify-between">
                      <span>
                        <span
                          className="font-semibold text-amber-300"
                          data-testid="gcal-reconcile-future-count"
                        >
                          {futureCount}
                        </span>{" "}
                        upcoming booking{futureCount === 1 ? "" : "s"} to
                        recover
                      </span>
                      <span className="text-slate-500">
                        Recovering these creates their missing calendar events.
                      </span>
                    </div>
                    {futureCount > 0 && (
                      <table className="w-full text-xs">
                        <thead className="bg-slate-900/60 text-slate-500">
                          <tr>
                            <th className="text-left px-3 py-2">Customer</th>
                            <th className="text-left px-3 py-2">Date</th>
                            <th className="text-left px-3 py-2">Time</th>
                            <th className="text-left px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody
                          className="divide-y divide-slate-800"
                          data-testid="gcal-reconcile-future-list"
                        >
                          {future.slice(0, 50).map((m) => (
                            <tr key={m.booking_id} className="text-slate-200">
                              <td className="px-3 py-2">
                                {m.customer_name || "—"}
                                <div className="text-[10px] font-mono text-slate-500">
                                  {m.booking_id?.slice(0, 20)}…
                                </div>
                              </td>
                              <td className="px-3 py-2">{m.booking_date || "—"}</td>
                              <td className="px-3 py-2">{m.booking_time || "—"}</td>
                              <td className="px-3 py-2">
                                {m.gcal_sync_status === "failed" ? (
                                  <span className="text-red-300">
                                    {m.gcal_sync_error?.slice(0, 40) || "failed"}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">never attempted</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {future.length > 50 && (
                      <p className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-800">
                        Showing first 50 of {future.length}. Confirming
                        recovers all.
                      </p>
                    )}
                  </div>

                  {/* Past bucket — collapsed by default, opt-in via checkbox.
                      Recovering past bookings usually just creates noise on
                      the operational calendar, so it's off by default. */}
                  {pastCount > 0 && (
                    <div
                      className="rounded-md border border-slate-800 overflow-hidden"
                      data-testid="gcal-reconcile-past-block"
                    >
                      <div className="bg-slate-800/60 px-3 py-2 text-xs text-slate-300">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includePast}
                            onChange={(e) => setIncludePast(e.target.checked)}
                            className="accent-saturn-orange mt-0.5"
                            data-testid="gcal-reconcile-include-past"
                          />
                          <span>
                            Also recover{" "}
                            <span
                              className="font-semibold text-amber-300"
                              data-testid="gcal-reconcile-past-count"
                            >
                              {pastCount}
                            </span>{" "}
                            past booking{pastCount === 1 ? "" : "s"}{" "}
                            <span className="text-slate-500">
                              (already happened — usually just adds noise to
                              the calendar)
                            </span>
                          </span>
                        </label>
                      </div>
                      {includePast && past.length > 0 && (
                        <table className="w-full text-xs">
                          <thead className="bg-slate-900/60 text-slate-500">
                            <tr>
                              <th className="text-left px-3 py-2">Customer</th>
                              <th className="text-left px-3 py-2">Date</th>
                              <th className="text-left px-3 py-2">Time</th>
                            </tr>
                          </thead>
                          <tbody
                            className="divide-y divide-slate-800"
                            data-testid="gcal-reconcile-past-list"
                          >
                            {past.slice(0, 20).map((m) => (
                              <tr key={m.booking_id} className="text-slate-300">
                                <td className="px-3 py-2">
                                  {m.customer_name || "—"}
                                  <div className="text-[10px] font-mono text-slate-500">
                                    {m.booking_id?.slice(0, 20)}…
                                  </div>
                                </td>
                                <td className="px-3 py-2">{m.booking_date || "—"}</td>
                                <td className="px-3 py-2">{m.booking_time || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {includePast && past.length >= 20 && (
                        <p className="text-[11px] text-slate-500 px-3 py-2 border-t border-slate-800">
                          Showing first 20 of {pastCount}. Confirming
                          recovers all.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={close}
              disabled={loading}
              className="border-slate-700 text-slate-300"
              data-testid="gcal-reconcile-close-btn"
            >
              {result ? "Done" : "Cancel"}
            </Button>
            {/* Confirm only surfaces on the preview view AND only when
                there's something to recover (respecting includePast)
                AND gcal is configured. */}
            {!result && plan && targetCount > 0 && plan.gcal_configured && (
              <Button
                type="button"
                onClick={confirmRecover}
                disabled={loading}
                className="bg-saturn-orange hover:bg-saturn-orange/90 text-black"
                data-testid="gcal-reconcile-confirm-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Recovering…
                  </>
                ) : (
                  `Confirm & recover ${targetCount}${includePast ? " (incl. past)" : ""}`
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CalendarReconcileButton;
